import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Fragment,
} from 'react';
import { TModal } from './Modal';
import { CSSTransition } from 'react-transition-group';
import { Box, ThemeUICSSObject, getVariant } from '@apia/theme';
import DialogHeader from './layout/DialogHeader';
import { ModalContext } from './ModalContext';
import { useEscapeKey } from './hooks/useEscapeKey';
import { useInitialFocus } from './hooks/useInitialFocus';
import { getFocusSelector, useCombinedRefs } from '@apia/util';
import { useStyleState } from './hooks/useStyleState';
import { uniqueId } from '@apia/notifications';
import { FreeFocusInside } from 'react-focus-lock';
import { handleMouseMove } from './windowModal/handleMouseMove';
import { bringToTop } from './windowModal/bringToTop';

export const WindowModal = forwardRef<HTMLDivElement, TModal>(
  (
    {
      animationDuration = 150,
      children = null,
      className,
      defaultPosition,
      dragBounded,
      hideCloseButton,
      id,
      initialFocusConfiguration,
      initialFocusGetter,
      initialFocusRef,
      isOpen = false,
      maxWidth,
      NavBar,
      noFocus,
      noHeader = false,
      onClose = () => {},
      onDragStart = () => {},
      onExited = () => {},
      Portal,
      shouldCloseOnEsc = true,
      size = 'md',
      title = '',
      variant,
    },
    ref,
  ) => {
    const innerId = useMemo(() => {
      return `Modal${uniqueId()}`;
    }, []);

    const lastTitle = useRef(title);
    const [innerTitle, setInnerTitle] = useState(title);
    if (lastTitle.current !== title && innerTitle !== title) {
      setInnerTitle(title);
    }
    const [isOpenInner, setIsOpen] = useState(isOpen);
    useEffect(() => {
      setIsOpen(isOpen);
    }, [isOpen]);

    const closeHandler = useCallback(() => {
      setIsOpen(false);
      onClose();
    }, [onClose]);

    const [parentElement, setParentElement] = useState<HTMLElement | null>(
      null,
    );

    useEscapeKey({ closeHandler, id: innerId, isOpenInner, shouldCloseOnEsc });
    useInitialFocus({
      isOpenInner,
      initialFocusConfiguration,
      initialFocusGetter,
      initialFocusRef,
      noFocus,
      parentElement,
    });

    const Wrapper = useMemo(() => {
      return Portal ?? Fragment;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasSetPosition = useRef(false);
    const { ref: stylesRef, setState: setStyle } = useStyleState({
      onUpdateRef(el) {
        const mainElement = el.querySelector<HTMLElement>('.modal__main');
        const bounding = mainElement!.getBoundingClientRect();

        const returnObject: Partial<ThemeUICSSObject> = {
          position: 'fixed',
        };

        if (!hasSetPosition.current && mainElement) {
          bringToTop(mainElement);
          hasSetPosition.current =
            !!defaultPosition || (!!bounding && bounding.width > 200);
          Object.assign(
            returnObject,
            defaultPosition ?? {
              left: `${window.innerWidth / 2 - (bounding?.width ?? 0) / 2}px`,
              top: `${window.innerHeight / 2 - (bounding?.height ?? 0) / 2}px`,
            },
          );
        }

        return returnObject;
      },
    });

    const unSubscribe = useRef(() => {});
    const initialPosition = useRef<null | DOMRect>(null);
    const initialMousePosition = useRef<null | { x: number; y: number }>(null);
    const assignRefs = useCombinedRefs(
      setParentElement as unknown as React.ForwardedRef<unknown>,
      ref as unknown as React.Ref<unknown>,
      stylesRef as unknown as React.Ref<unknown>,
    );

    return (
      <Wrapper>
        <ModalContext.Provider
          value={useMemo(() => {
            return {
              changeTitle: (newTitle) => {
                setInnerTitle(newTitle);
              },
              close: () => {
                closeHandler();
              },
              descriptionId: `${innerId}_Description`,
            };
          }, [closeHandler, innerId])}
        >
          <CSSTransition
            in={isOpen}
            timeout={animationDuration}
            classNames="modal"
            appear
            unmountOnExit
            nodeRef={setParentElement as any}
            onExited={onExited}
          >
            <Box
              ref={assignRefs}
              className={`modal__variantWrapper window__modal ${className ?? ''}`}
              id={`modal__${innerId}`}
              {...(variant ? getVariant(variant) : {})}
              sx={{ zIndex: 'modal' }}
            >
              <FreeFocusInside>
                <Box
                  id={id}
                  role="dialog"
                  aria-modal="true"
                  aria-label={title}
                  aria-describedby={`${innerId}_Description`}
                  aria-hidden="false"
                  className="modal__main"
                  // allows scrolling within element, despite body lock
                  data-scroll-lock-scrollable
                  sx={useMemo(() => {
                    return {
                      variant: `layout.common.modals.${size}`,
                      ...(maxWidth !== undefined ? { maxWidth } : {}),
                    };
                  }, [maxWidth, size])}
                  data-variant={`layout.common.modals.${size}`}
                  ref={useCallback(
                    (el: null | HTMLElement) => {
                      unSubscribe.current();
                      unSubscribe.current = () => {};

                      if (el) {
                        const handleMouseDown = (ev: MouseEvent) => {
                          if (
                            (ev.target as HTMLElement).closest?.(
                              '[draggable="true"]',
                            ) ||
                            !(ev.target as HTMLElement).closest?.(
                              '.modal__header',
                            )
                          )
                            return;

                          if (
                            (ev.target as HTMLElement).closest(
                              getFocusSelector(),
                            )
                          )
                            return;

                          const res = onDragStart?.(ev);
                          if (res === false) return;

                          const rect = el.getBoundingClientRect();
                          initialPosition.current = rect;
                          initialMousePosition.current = {
                            x: ev.clientX,
                            y: ev.clientY,
                          };

                          bringToTop(
                            (ev.target as HTMLElement).closest<HTMLElement>(
                              '.modal__main',
                            )!,
                          );
                        };
                        const innerHandleMouseMove = (ev: MouseEvent) => {
                          if (initialPosition.current) {
                            handleMouseMove({
                              dragBounded,
                              ev,
                              initialMousePosition,
                              initialPosition,
                              modalMainElement: el,
                              setStyle,
                            });
                          }
                        };

                        const handleMouseUp = () => {
                          initialPosition.current = null;
                        };

                        el.addEventListener('mousedown', handleMouseDown);
                        document.addEventListener(
                          'mousemove',
                          innerHandleMouseMove,
                        );
                        document.addEventListener('mouseup', handleMouseUp);

                        unSubscribe.current = () => {
                          el.removeEventListener('mousedown', handleMouseDown);
                          document.removeEventListener(
                            'mousemove',
                            innerHandleMouseMove,
                          );
                          document.removeEventListener(
                            'mouseup',
                            handleMouseUp,
                          );
                        };
                      }
                    },
                    [dragBounded, onDragStart, setStyle],
                  )}
                >
                  {!noHeader && (
                    <DialogHeader
                      NavBar={NavBar}
                      title={innerTitle}
                      close={closeHandler}
                      hideCloseButton={hideCloseButton}
                      id={`${innerId}_Header`}
                    />
                  )}
                  <Box
                    className="modal__content"
                    id={
                      typeof children === 'string'
                        ? `${innerId}_Description`
                        : undefined
                    }
                  >
                    {children}
                  </Box>
                </Box>
              </FreeFocusInside>
            </Box>
          </CSSTransition>
        </ModalContext.Provider>
      </Wrapper>
    );
  },
);
